import type { ComponentChildren } from "preact";
import { useMemo, useRef, useState } from "preact/hooks";
import type { CoreData, NameEntry, SexPref, Verdict } from "../data/types";
import { useStore } from "../state/store";
import { decide, decisions, listByVerdict, ratings, settings, shareOrCopy, siteOrigin, toast } from "../state/app";
import { matchesOf, partner, partnerName, partnerSets } from "../state/partner";
import { goTab, openName, openOverlay } from "../state/router";
import { SEX_WORD, decodeSpark, fmt, genderClass, sexIdxFor, trendInfo } from "../data/format";
import { entryFor } from "../data/load";
import { Sparkline } from "./charts";
import { Segmented } from "./FilterSheet";
import { Sheet } from "./Sheet";
import { IconArrow, IconHeart, IconMore, IconShare, IconStar, IconTrophy, IconX, IconRefresh } from "./icons";
import { ChosenBanner } from "./Chosen";

/** Your verdict on a name, for lists that mix names you've decided on with ones you haven't. */
export function VerdictMark({ d }: { d: Verdict | undefined }) {
  if (d === 2)
    return (
      <span class="row-badge love">
        <IconStar size={14} filled />
        <span class="sr-only">, you love this</span>
      </span>
    );
  if (d === 1)
    return (
      <span class="row-badge like">
        <IconHeart size={12} filled />
        <span class="sr-only">, you like this</span>
      </span>
    );
  if (d === -1)
    return (
      <span class="row-badge nope">
        <IconX size={12} />
        <span class="sr-only">, you passed on this</span>
      </span>
    );
  return null;
}

export function NameRow({
  e,
  core,
  pref,
  badge,
  right,
  onOpen,
  rank,
}: {
  e: NameEntry;
  core: CoreData;
  pref: SexPref;
  badge?: ComponentChildren;
  right?: ComponentChildren;
  onOpen?: () => void;
  rank?: number;
}) {
  const si = sexIdxFor(e, pref);
  const t = trendInfo(e.t[si]);
  return (
    <li class={`name-row g-${genderClass(e)}`} data-key={e.key}>
      <button class="row-main" onClick={onOpen ?? (() => openName(e.key))}>
        {rank !== undefined && <span class="row-rank">{rank}</span>}
        <span class="row-dot" aria-hidden="true" />
        <span class="row-text">
          <span class="row-name">
            {e.name}
            {badge}
          </span>
          <span class="row-sub">
            {e.r[si] ? `#${fmt(e.r[si])} ${SEX_WORD[si]}` : `Rare in ${core.latest}`}
            {t.dir !== "none" && (
              <span class={`row-trend t-${t.dir}`}>
                {" · "}
                <IconArrow dir={t.dir} size={12} /> {t.label}
              </span>
            )}
          </span>
        </span>
        <Sparkline values={decodeSpark(e.s[si] || e.s[si ? 0 : 1])} height={26} class="row-spark" />
      </button>
      {right}
    </li>
  );
}

/** A few plain-English observations about the names someone likes. */
function tasteNotes(entries: NameEntry[], pref: SexPref, latest: number): string[] {
  if (entries.length < 10) return [];
  const notes: string[] = [];
  const ranks = entries.map((e) => e.r[sexIdxFor(e, pref)] || 99999).sort((a, b) => a - b);
  const median = ranks[Math.floor(ranks.length / 2)];
  if (median <= 100) notes.push(`You like familiar names: half your picks are in the ${latest} top 100.`);
  else if (median <= 500) notes.push(`You like names that are known but not everywhere: most of your picks rank around #${fmt(median)}.`);
  else if (median <= 2000) notes.push(`You lean towards less common names: most of your picks rank around #${fmt(median)}.`);
  else notes.push("You like rare names: most of your picks were given to only a handful of babies.");
  const rising = entries.filter((e) => (e.t[sexIdxFor(e, pref)] ?? 0) >= 25).length;
  const pct = Math.round((rising / entries.length) * 100);
  if (pct >= 40) notes.push(`${pct}% of your picks are on the rise. Expect to hear them more.`);
  const letters = new Map<string, number>();
  entries.forEach((e) => letters.set(e.initial, (letters.get(e.initial) ?? 0) + 1));
  const top = [...letters.entries()].sort((a, b) => b[1] - a[1]).slice(0, 2);
  if (top[0] && top[0][1] >= 3)
    notes.push(`Favourite first letter${top[1] && top[1][1] >= 3 ? "s" : ""}: ${top.filter((t) => t[1] >= 3).map((t) => t[0]).join(" and ")}.`);
  const syl = new Map<number, number>();
  entries.forEach((e) => syl.set(Math.min(e.syl, 4), (syl.get(Math.min(e.syl, 4)) ?? 0) + 1));
  const [s1, n1] = [...syl.entries()].sort((a, b) => b[1] - a[1])[0];
  if (n1 / entries.length >= 0.5) notes.push(`Most have ${s1 === 4 ? "four or more" : ["", "one", "two", "three"][s1]} syllable${s1 === 1 ? "" : "s"}.`);
  const origins = new Map<string, number>();
  entries.forEach((e) => e.origin && e.origin !== "Modern" && origins.set(e.origin, (origins.get(e.origin) ?? 0) + 1));
  const o = [...origins.entries()].sort((a, b) => b[1] - a[1]);
  if (o[0] && o[0][1] >= 4) notes.push(`The most common origin among them is ${o[0][0]}.`);
  return notes.slice(0, 4);
}

type ListTab = "loved" | "liked" | "matches" | "passed";
type SortBy = "recent" | "ranking" | "popular" | "az";

function shareText(core: CoreData, keys: string[], map: Record<string, { d: Verdict; n?: string }>): string {
  const lines = keys
    .map((k) => entryFor(core, k, map[k]?.n))
    .map((e) => `${map[e.key]?.d === 2 ? "★" : "♥"} ${e.name}`);
  return `Our baby name shortlist:\n${lines.join("\n")}\n\nMade with Nameling${__PREVIEW__ ? "" : `: ${siteOrigin()}`}`;
}

const PAGE = 150;

export function ShortlistView({ core }: { core: CoreData }) {
  const map = useStore(decisions, (s) => s.map);
  const pref = useStore(settings, (s) => s.sex);
  const pSets = useStore(partnerSets, (s) => s);
  const paired = useStore(partner, (s) => !!s.code);
  const elo = useStore(ratings, (s) => s.elo);
  const hints = useStore(settings, (s) => s.partnerHints);
  // open on Loved, unless nothing is loved yet but something is liked
  const [tab, setTab] = useState<ListTab>(() => {
    const verdicts = Object.values(decisions.get().map).map((d) => d.d);
    return !verdicts.includes(2) && verdicts.includes(1) ? "liked" : "loved";
  });
  const [sort, setSort] = useState<SortBy>("recent");
  // long lists (Passed can run to thousands) show a page at a time: each row draws a chart
  const [shown, setShown] = useState(PAGE);
  const [shownFor, setShownFor] = useState(tab);
  if (shownFor !== tab) {
    setShownFor(tab);
    setShown(PAGE);
  }
  const [menu, setMenu] = useState<string | null>(null);

  const loved = useMemo(() => listByVerdict(map, [2]), [map]);
  const liked = useMemo(() => listByVerdict(map, [1]), [map]);
  const passed = useMemo(() => listByVerdict(map, [-1]), [map]);
  const matches = useMemo(() => matchesOf(map, pSets), [map, pSets]);
  const hasPartner = pSets.others.length > 0;

  const keys = tab === "loved" ? loved : tab === "liked" ? liked : tab === "matches" ? matches : passed;
  const entries = useMemo(() => {
    const list = keys.map((k) => entryFor(core, k, map[k]?.n));
    const si = (e: NameEntry) => sexIdxFor(e, pref);
    if (sort === "az") list.sort((a, b) => a.name.localeCompare(b.name, "en-GB"));
    else if (sort === "popular") list.sort((a, b) => (a.r[si(a)] || 1e9) - (b.r[si(b)] || 1e9));
    else if (sort === "ranking") list.sort((a, b) => (elo[b.key] ?? 1500) - (elo[a.key] ?? 1500));
    return list;
  }, [keys, sort, elo, pref, map]);

  const shortlistSize = loved.length + liked.length;
  const taste = useMemo(
    () =>
      tasteNotes(
        [...loved, ...liked].map((k) => entryFor(core, k, map[k]?.n)),
        pref,
        core.latest,
      ),
    [loved, liked, pref, core],
  );
  const doShare = () => {
    const text = shareText(core, [...loved, ...liked], map);
    shareOrCopy({ title: "Our baby name shortlist", text }, text, "Shortlist copied to clipboard");
  };

  const menuEntry = menu ? entryFor(core, menu, map[menu]?.n) : undefined;
  // a row that leaves the list hands focus to the next row (or the one before, or the heading)
  const after = useRef<HTMLElement | null>(null);
  const act = (k: string, v: Verdict | null, msg: string) => {
    const li = document.querySelector<HTMLElement>(`.list-view li[data-key="${CSS.escape(k)}"]`);
    const sib = li?.nextElementSibling ?? li?.previousElementSibling;
    after.current = sib?.querySelector<HTMLElement>(".row-main") ?? document.querySelector<HTMLElement>(".list-view .view-title");
    const hadFocus = !!li?.contains(document.activeElement);
    decide(k, v);
    setMenu(null);
    toast(msg);
    if (hadFocus) requestAnimationFrame(() => after.current?.focus());
  };

  const tabs: { v: ListTab; label: string }[] = [
    { v: "loved", label: `Loved ${loved.length}` },
    { v: "liked", label: `Liked ${liked.length}` },
  ];
  if (paired) tabs.push({ v: "matches", label: `Matches ${matches.length}` });
  tabs.push({ v: "passed", label: `Passed ${passed.length}` });

  return (
    <section class="list-view" aria-label="Your shortlist">
      <div class="view-head tight">
        <h1 class="view-title" tabIndex={-1}>
          Shortlist
        </h1>
        <div class="row-gap">
          <button class="btn btn-small" onClick={() => openOverlay("nameoff")} disabled={shortlistSize < 2}>
            <IconTrophy size={16} /> Name-off
          </button>
          <button class="icon-btn" onClick={doShare} disabled={!shortlistSize} aria-label="Share your shortlist">
            <IconShare size={20} />
          </button>
        </div>
      </div>
      <p class="view-sub under">
        {shortlistSize ? `${fmt(shortlistSize)} name${shortlistSize === 1 ? "" : "s"} you'd consider` : "Names you like will land here"}
      </p>

      <ChosenBanner core={core} />

      {taste.length > 0 && (tab === "loved" || tab === "liked") && (
        <section class="taste" aria-label="Your taste">
          <h2>Your taste so far</h2>
          <ul>
            {taste.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </section>
      )}

      <div class="list-controls">
        <Segmented<ListTab> label="Which list" value={tab} onChange={setTab} options={tabs} />
        {entries.length > 1 && (
          <label class="sort-select">
            <span>Sort</span>
            <select value={sort} onChange={(ev) => setSort((ev.target as HTMLSelectElement).value as SortBy)}>
              <option value="recent">Newest first</option>
              <option value="ranking">My Name-off ranking</option>
              <option value="popular">Most popular</option>
              <option value="az">A to Z</option>
            </select>
          </label>
        )}
      </div>

      {entries.length === 0 ? (
        <div class="empty">
          {tab === "loved" && (
            <>
              <IconStar size={32} />
              <p>Your favourites go here, above the names you like. Swipe up on a name, or tap Love, to add it.</p>
            </>
          )}
          {tab === "liked" && (
            <>
              <IconHeart size={32} />
              <p>Swipe right on names you'd consider.</p>
            </>
          )}
          {tab === "matches" && (
            <>
              <IconHeart size={32} />
              <p>
                {hasPartner
                  ? `No matches yet. Keep swiping: when you and ${partnerName()} both like a name, it shows up here.`
                  : "Waiting for your partner to join. Matches appear here once you're both swiping."}
              </p>
            </>
          )}
          {tab === "passed" && (
            <>
              <IconX size={32} />
              <p>Names you swipe left on are kept here, in case you change your mind.</p>
            </>
          )}
          {tab !== "passed" && (
            <button class="btn btn-primary" onClick={() => goTab("swipe")}>
              Start swiping
            </button>
          )}
        </div>
      ) : (
        <>
        <ul class="rows">
          {entries.slice(0, shown).map((e, i) => (
            <NameRow
              key={e.key}
              e={e}
              core={core}
              pref={pref}
              rank={sort === "ranking" ? i + 1 : undefined}
              badge={
                <>
                  {map[e.key]?.d === 2 && tab !== "loved" && (
                    <>
                      <IconStar size={14} filled class="row-badge love" />
                      <span class="sr-only">, loved</span>
                    </>
                  )}
                  {hasPartner && pSets.liked.has(e.key) && tab !== "matches" && (tab !== "passed" || hints) && (
                    <span class="row-badge partner">
                      <IconHeart size={12} filled />
                      <span class="sr-only">, {partnerName()} likes this</span>
                    </span>
                  )}
                </>
              }
              right={
                tab === "passed" ? (
                  <button class="icon-btn" onClick={() => act(e.key, null, `${e.name} is back in your deck`)} aria-label={`Give ${e.name} another chance`}>
                    <IconRefresh size={18} />
                  </button>
                ) : (
                  <button class="icon-btn" onClick={() => setMenu(e.key)} aria-label={`Options for ${e.name}`}>
                    <IconMore size={20} />
                  </button>
                )
              }
            />
          ))}
        </ul>
        {entries.length > shown && (
          <button class="btn btn-wide more-btn" onClick={() => setShown(shown + PAGE)}>
            Show {fmt(Math.min(PAGE, entries.length - shown))} more
          </button>
        )}
        </>
      )}

      <Sheet open={!!menuEntry} onClose={() => setMenu(null)} title={menuEntry?.name ?? ""} size="auto" restoreTo={after}>
        {menuEntry && (
          <div class="action-list">
            {map[menuEntry.key]?.d !== 2 && (
              <button onClick={() => act(menuEntry.key, 2, `Loved ${menuEntry.name}`)}>
                <IconStar size={20} filled /> Move to Loved
              </button>
            )}
            {map[menuEntry.key]?.d !== 1 && (
              <button onClick={() => act(menuEntry.key, 1, `Moved ${menuEntry.name} to Liked`)}>
                <IconHeart size={20} filled /> Move to Liked
              </button>
            )}
            <button onClick={() => act(menuEntry.key, -1, `Passed on ${menuEntry.name}`)}>
              <IconX size={20} /> Pass on it
            </button>
            <button onClick={() => act(menuEntry.key, null, `${menuEntry.name} is back in your deck`)}>
              <IconRefresh size={20} /> Put back in the deck
            </button>
            <button
              onClick={() => {
                const k = menuEntry.key;
                setMenu(null);
                window.setTimeout(() => openName(k), 240);
              }}
            >
              <IconArrow dir="flat" size={20} /> See details
            </button>
          </div>
        )}
      </Sheet>
    </section>
  );
}
